import { BACKEND_URL } from '../../../configs'
import { alertError, createJsonHeaders, alertSuccess, logoutOnRequestOrResponseJson } from '../../../api'
import {
  saveCareProfessionals,
  saveServiceUsers,
  saveTotalElements
} from '../actions/serviceUsersActions'
import { CareProfessionalType, ServiceUserType } from 'models/ServiceUsersTypes'
import { DatePeriodType } from '../../professionals/model/usersModel'
import { DEFAULT_ROWS_PER_PAGE, normalizePageSize } from '../../../constants/pagination'

type GetAllPatientType = {
  data: {
    content: ServiceUserType[]
    totalPages: number
    totalElements: number
    last: boolean
    size: number
    number: number
    pageable: any
    sort: any
    numberOfElements: number
    first: boolean
    empty: boolean
  }
}

export type ServiceUserDataType = {
  firstName: string
  lastName: string
  botId: number
  assignedProfessionals: number[]
}

export const getServiceUsers = async (
  botId: string,
  page: number,
  search = '',
  size: number | string = DEFAULT_ROWS_PER_PAGE,
  signal?: AbortSignal
) => {
  const encodedQuery = encodeURIComponent(search)

  const rowsPerPage = normalizePageSize(size)
  return fetch(`${BACKEND_URL}/patients/${botId}/all?page=${page}&search=${encodedQuery}&size=${rowsPerPage}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
    signal
  }).then(response => {
    if (response.status === 200) {
      return logoutOnRequestOrResponseJson(response).then(({ data }: GetAllPatientType) => {
        saveServiceUsers(data.content)
        saveTotalElements(data.totalElements)
      })
    }
    response.text().then(text => alertSuccess(text))
  }).catch(error => {
    if (error?.name === 'AbortError') throw error
    alertError('Sorry but something going wrong please ping support!')
  })
}

export const getAllCareProfessionals = async (botId: string) => {
  return fetch(`${BACKEND_URL}/users/management/all/${botId}/values`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET'
  }).then(response => {
    if (response.status === 200) {
      return logoutOnRequestOrResponseJson(response).then((json: CareProfessionalType[]) => {
        saveCareProfessionals(json)
      })
    }
    response.text().then(text => alertSuccess(text))
  }).catch(() => {
    alertError('Sorry but something going wrong please ping support!')
  })
}

export const deleteUsers = async (botId: string, patentIds: string) => {
  return fetch(`${BACKEND_URL}/patients/${botId}/delete?patientIdsParam=${patentIds}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'DELETE'
  }).then(response => {
    if (response.status === 200) {
      return logoutOnRequestOrResponseJson(response).then(() => {
        alertSuccess('You have successfully removed the service user(s) from CareBrain', 2000)
      })
    }
  }).catch(() => {
    alertError('Sorry but something going wrong please ping support!')
  })
}

export const createServiceUser = async (botId: string, userData: ServiceUserDataType, isNewUser = false): Promise<Response> => {
  return fetch(`${BACKEND_URL}/patients/${botId}/create`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(userData)
  }).then((response) => {
    if (response.status === 200) {
      return logoutOnRequestOrResponseJson(response).then((json) => {
        alertSuccess(isNewUser ? 'User created' : 'User updated', 2000)
        return json
      })
    }
    response.text().then(text => alertError(text))
  }).catch(() => {
    alertError('Sorry but something going wrong please ping support!')
  })
}

export const serviceUsersMultiCreating = async (botId: string, file: FormData) => {
  return fetch(`${BACKEND_URL}/patients/${botId}/bulk`, {
    credentials: 'include',
    method: 'POST',
    body: file
  }).then(response => {
    return response
  }).catch(() => {
    alertError('Sorry but something going wrong please ping support!')
  })
}

export const uploadPatientKnowledge = async (botId: string, patientId: number, file: FormData) => {
  return fetch(`${BACKEND_URL}/private/bot/${botId}/knowledge/upload?level=CARE_PLAN&patientId=${patientId}&train=true`, {
    credentials: 'include',
    method: 'POST',
    body: file
  }).then(response => {
    if (response.ok) {
      alertSuccess('Successfully uploaded', 2000)
    }
    return response
  }).catch(() => {
    alertError('Sorry but something going wrong please ping support!')
  })
}

export const deletePatientCarePlan = async (botId: string, sourceId: string) => {
  return fetch(`${BACKEND_URL}/private/bot/${botId}/knowledge/?source_id=${sourceId}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'DELETE'
  }).then(response => {
    if (response.status === 200) {
      return logoutOnRequestOrResponseJson(response).then(() => {
        alertSuccess('You successfully removed the care plan.', 2000)
      })
    } else if (response.status >= 400) {
      response.text().then(text => {
        const innerJson: string = JSON.parse(text).error.message.match(/{.*}/)
        alertError(JSON.parse(innerJson[0]).error || 'Error while removing the care plan')
      })
    }
  })
}

export const showDuplicates = async (botId: string) => {
  return fetch(`${BACKEND_URL}/patients/${botId}/duplicates`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET'
  }).then(response => {
    return response.json()
  }).catch((err) => {
    console.error('Sorry but something going wrong please ping support!', err)
  })
}

export const multipleCareProfessionalsAssign = async (botId: string, professionalId: string, patientId: string, method: 'POST' | 'DELETE') => {
  return fetch(`${BACKEND_URL}/patients/${botId}/assign?userIdsParam=${professionalId}&patientIdsParam=${patientId}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: method
  }).then(response => {
    if (response.ok) {
      if (method == 'POST') {
        alertSuccess('Professionals successfully added to selected users.', 2000)
      } else {
        alertSuccess('Professionals successfully removed for selected users.', 2000)
      }
    }
    return response.json()
  }).catch((err) => {
    console.error('Sorry but something going wrong please ping support!', err)
  })
}

export const exportConversations = async (botId: string, userId: number, from: string, to: string) => {
  return fetch(`${BACKEND_URL}/patients/${botId}/user/${userId}?from=${from}&to=${to}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
  }).then(response => {
    if (!response.ok) {
      alertError('Error exporting conversation logs')
    }
    return response.blob()
  }).then(blob => {
    const reader = new FileReader()
    reader.onload = function () {
      if (reader.result instanceof ArrayBuffer) {
        const utf8Blob = new Blob([new Uint8Array(reader.result)], { type: 'text/csv;charset=utf-8' })

        const url = window.URL.createObjectURL(utf8Blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'chat_history.csv'
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } else {
        console.error('Unexpected FileReader result type', reader.result)
      }
    }
    reader.readAsArrayBuffer(blob)
  }).catch(() => {
    alertError('Error exporting conversation logs')
  })
}
